<template>
  <v-row>
    <v-col cols="12">
      <v-card class="fa-card" flat>
        <!-- HEADER BAND -->
        <div class="fa-header pl-base-bg">
          <div class="fa-header-icon">
            <v-icon color="#fff" size="22">mdi-desk-lamp</v-icon>
          </div>
          <div>
            <div class="fa-title">Fixed Assets</div>
            <div class="fa-subtitle">Asset register and straight-line depreciation</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="info" outlined dense border="left" class="fa-disclaimer">
            Straight-line depreciation only. Disposal simply stops future
            depreciation — gain/loss on sale is not posted. Verify with
            your accountant before relying on this for tax purposes.
          </v-alert>

          <div class="fa-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" class="fa-actions">
                <v-btn outlined color="#0a5a90" class="mr-2" :loading="runningDepreciation" @click="depreciationDialog = true">
                  <v-icon left size="16">mdi-calculator-variant-outline</v-icon>
                  Run Depreciation
                </v-btn>
                <v-btn class="fa-add-btn" @click="openCreateDialog">
                  <v-icon left size="16">mdi-plus</v-icon>
                  Add Asset
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <app-ag-grid
            title="Fixed Assets"
            :columnDefs="columnDefs"
            :rowData="assets"
            :newBtn="false"
            :refreshBtn="false"
            :mobileCard="mobileCard"
            height="height: 480px"
          />
        </div>
      </v-card>
    </v-col>

    <!-- CREATE/EDIT DIALOG -->
    <global-dialog
      v-if="assetDialog"
      v-model="assetDialog"
      :title="editingAsset ? 'Edit Asset' : 'Add Fixed Asset'"
      icon="mdi-desk-lamp"
      width="640"
      :buttons="assetDialogButtons"
    >
      <v-form ref="form" v-model="valid">
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.name" label="Asset Name" outlined dense :rules="[rules.required]" />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="form.category" label="Category" outlined dense />
          </v-col>

          <template v-if="!editingAsset">
            <v-col cols="12" sm="6">
              <v-menu v-model="purchaseDateMenu" :close-on-content-click="false" offset-y max-width="290" min-width="290">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedPurchaseDate"
                    label="Purchase Date"
                    readonly
                    outlined
                    dense
                    :rules="[rules.required]"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.purchaseDate" @input="purchaseDateMenu = false" />
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.purchaseCost"
                label="Purchase Cost (₹)"
                type="number"
                outlined
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.salvageValue" label="Salvage Value (₹)" type="number" outlined dense />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.usefulLifeMonths"
                label="Useful Life (months)"
                type="number"
                outlined
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.assetAccountId"
                :items="fixedAssetAccounts"
                item-text="name"
                item-value="_id"
                label="Asset Account"
                outlined
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.paymentAccountId"
                :items="paymentAccounts"
                item-text="name"
                item-value="_id"
                label="Paid From"
                outlined
                dense
                :rules="[rules.required]"
              />
            </v-col>
          </template>

          <v-col cols="12">
            <v-textarea v-model="form.note" label="Note" outlined dense rows="2" />
          </v-col>
        </v-row>
      </v-form>
    </global-dialog>

    <!-- DEPRECIATION RUN DIALOG -->
    <global-dialog
      v-if="depreciationDialog"
      v-model="depreciationDialog"
      title="Run Depreciation"
      icon="mdi-calculator-variant-outline"
      width="420"
      :buttons="depreciationDialogButtons"
    >
      <v-menu v-model="depreciationAsOfMenu" :close-on-content-click="false" offset-y max-width="290" min-width="290">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-model="formattedAsOf"
            label="As of"
            readonly
            outlined
            dense
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker v-model="depreciationAsOf" type="month" @input="depreciationAsOfMenu = false" />
      </v-menu>
      <div class="fa-empty-state">
        Posts depreciation for every active asset, for every unposted month up to and including this one.
      </div>
    </global-dialog>

    <!-- DISPOSE CONFIRM -->
    <global-dialog
      v-if="disposeDialog"
      v-model="disposeDialog"
      title="Dispose Asset?"
      icon="mdi-delete-outline"
      width="420"
      :buttons="disposeDialogButtons"
    >
      <p>
        This marks "{{ assetToDispose?.name }}" as disposed and stops
        future depreciation runs from touching it. Already-posted
        depreciation history is unaffected.
      </p>
    </global-dialog>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";
import GlobalDialog from "@/components/common/global-dialog.vue";

export default {
  name: "FixedAssetsPage",
  components: { AppAgGrid, GlobalDialog },

  data() {
    return {
      assetDialog: false,
      depreciationDialog: false,
      disposeDialog: false,
      valid: false,
      saving: false,
      runningDepreciation: false,
      disposing: false,

      editingAsset: null,
      assetToDispose: null,
      depreciationAsOf: new Date().toISOString().slice(0, 7),
      purchaseDateMenu: false,
      depreciationAsOfMenu: false,

      form: {
        name: "",
        category: "",
        purchaseDate: new Date().toISOString().slice(0, 10),
        purchaseCost: null,
        salvageValue: 0,
        usefulLifeMonths: 36,
        assetAccountId: null,
        paymentAccountId: null,
        note: "",
      },

      rules: {
        required: (v) => (v !== null && v !== undefined && v !== "") || "Required",
      },

      columnDefs: [
        { headerName: "Name", field: "name", minWidth: 160 },
        { headerName: "Category", field: "category", valueFormatter: (p) => p.value || "-", minWidth: 130 },
        { headerName: "Purchase Date", field: "purchaseDate", valueFormatter: (p) => this.formatDate(p.value), minWidth: 130 },
        { headerName: "Cost", field: "purchaseCost", valueFormatter: (p) => this.formatINR(p.value), minWidth: 130 },
        { headerName: "Accum. Depreciation", field: "accumulatedDepreciation", valueFormatter: (p) => this.formatINR(p.value), minWidth: 160 },
        {
          headerName: "Net Book Value",
          field: "netBookValue",
          valueGetter: (p) => p.data.purchaseCost - p.data.accumulatedDepreciation,
          valueFormatter: (p) => this.formatINR(p.value),
          minWidth: 150,
        },
        { headerName: "Status", field: "status", valueFormatter: (p) => (p.value || "").replace("_", " "), minWidth: 130 },
        {
          headerName: "Actions",
          field: "_id",
          minWidth: 100,
          sortable: false,
          filter: false,
          cellRenderer: (params) => {
            const disposeIcon =
              params.data.status !== "DISPOSED"
                ? `<i class="mdi mdi-delete-outline fa-dispose-asset" data-id="${params.data._id}" title="Dispose Asset" style="cursor: pointer; font-size: 18px; color: #d93025; margin-left: 12px;"></i>`
                : "";
            return `
              <i class="mdi mdi-pencil-outline fa-edit-asset" data-id="${params.data._id}" title="Edit Asset" style="cursor: pointer; font-size: 18px; color: #475569;"></i>
              ${disposeIcon}
            `;
          },
          onCellClicked: (params) => {
            if (params.event.target.classList.contains("fa-edit-asset")) {
              this.openEditDialog(params.data);
            } else if (params.event.target.classList.contains("fa-dispose-asset")) {
              this.confirmDispose(params.data);
            }
          },
        },
      ],
    };
  },

  computed: {
    assets() {
      return this.$store.state.fixedAssets.assets;
    },

    allAccounts() {
      return this.$store.state.accounts.accounts;
    },

    fixedAssetAccounts() {
      return this.allAccounts.filter((a) => a.type === "ASSET" && a.subType === "FIXED_ASSET" && !a.archived);
    },

    paymentAccounts() {
      return this.allAccounts.filter((a) => !a.archived && (a.isPaymentMethod || a.type === "ASSET"));
    },

    formattedPurchaseDate() {
      return this.formatDate(this.form.purchaseDate);
    },

    formattedAsOf() {
      if (!this.depreciationAsOf) return "";
      const d = new Date(`${this.depreciationAsOf}-01`);
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    },

    mobileCard() {
      const statusColors = {
        ACTIVE: { background: "#d1fae5", color: "#065f46" },
        FULLY_DEPRECIATED: { background: "#fef3c7", color: "#92400e" },
        DISPOSED: { background: "#e2e8f0", color: "#475569" },
      };

      return {
        title: (row) => row.name,
        subtitle: (row) => row.category || "-",
        badge: (row) => ({
          text: (row.status || "-").replace("_", " "),
          style: statusColors[row.status] || { background: "#f1f5f9", color: "#475569" },
        }),
        fields: (row) => [
          { label: "Cost", value: this.formatINR(row.purchaseCost) },
          { label: "Net Book Value", value: this.formatINR(row.purchaseCost - row.accumulatedDepreciation) },
        ],
        actions: (row) => {
          const actions = [
            { label: "Edit", icon: "mdi-pencil-outline", color: "#475569", onClick: () => this.openEditDialog(row) },
          ];
          if (row.status !== "DISPOSED") {
            actions.push({ label: "Dispose", icon: "mdi-delete-outline", color: "#d93025", onClick: () => this.confirmDispose(row) });
          }
          return actions;
        },
      };
    },

    assetDialogButtons() {
      return [
        { text: "Cancel", action: () => (this.assetDialog = false), outlined: true, color: "#0a5a90" },
        { text: "Save", action: this.submit, loading: this.saving },
      ];
    },

    depreciationDialogButtons() {
      return [
        { text: "Cancel", action: () => (this.depreciationDialog = false), outlined: true, color: "#0a5a90" },
        { text: "Run", action: this.doRunDepreciation, loading: this.runningDepreciation },
      ];
    },

    disposeDialogButtons() {
      return [
        { text: "Cancel", action: () => (this.disposeDialog = false), outlined: true, color: "#0a5a90" },
        { text: "Dispose", action: this.doDispose, color: "error", loading: this.disposing },
      ];
    },
  },

  created() {
    this.$store.dispatch("fixedAssets/getAssets");
    this.$store.dispatch("accounts/getAccounts");
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    openCreateDialog() {
      this.editingAsset = null;
      this.form = {
        name: "",
        category: "",
        purchaseDate: new Date().toISOString().slice(0, 10),
        purchaseCost: null,
        salvageValue: 0,
        usefulLifeMonths: 36,
        assetAccountId: this.fixedAssetAccounts[0]?._id || null,
        paymentAccountId: null,
        note: "",
      };
      this.assetDialog = true;
    },

    openEditDialog(asset) {
      this.editingAsset = asset;
      this.form = {
        name: asset.name,
        category: asset.category,
        note: asset.note,
      };
      this.assetDialog = true;
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        if (this.editingAsset) {
          await this.$store.dispatch("fixedAssets/updateAsset", {
            id: this.editingAsset._id,
            assetData: { name: this.form.name, category: this.form.category, note: this.form.note },
          });
          this.$toast?.success("Asset updated successfully");
        } else {
          await this.$store.dispatch("fixedAssets/createAsset", this.form);
          this.$toast?.success("Asset created successfully");
        }
        this.assetDialog = false;
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to save asset");
      } finally {
        this.saving = false;
      }
    },

    confirmDispose(asset) {
      this.assetToDispose = asset;
      this.disposeDialog = true;
    },

    async doDispose() {
      this.disposing = true;
      try {
        await this.$store.dispatch("fixedAssets/disposeAsset", this.assetToDispose._id);
        this.$toast?.success("Asset disposed successfully");
        this.disposeDialog = false;
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to dispose asset");
      } finally {
        this.disposing = false;
      }
    },

    async doRunDepreciation() {
      this.runningDepreciation = true;
      try {
        const result = await this.$store.dispatch("fixedAssets/runDepreciation", {
          asOf: `${this.depreciationAsOf}-01`,
        });
        this.$toast?.success(`Posted ${result.entriesPosted} depreciation entr${result.entriesPosted === 1 ? "y" : "ies"} across ${result.results.length} asset(s)`);
        this.depreciationDialog = false;
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to run depreciation");
      } finally {
        this.runningDepreciation = false;
      }
    },
  },
};
</script>

<style scoped>
.fa-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.fa-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.fa-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fa-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.fa-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.fa-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.fa-filter-bar {
  margin-bottom: 20px;
}

.fa-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

.fa-add-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.fa-empty-state {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 8px;
}
</style>
